
import { Timestamp } from "@firebase/firestore";
import { fetchItemsWithFilter, Filter } from "../api/generic";
import { GravitySample } from "../models/Sampling";
import { prefixCollection } from "../utils/prefix";
import { calculateABV, calculateFinalABV, calculateABVSimple, calculateABVSimpleNumber } from "../utils/steps";
import { HydroPiSample, iSpindelSample } from "../components/modules/HydroPi";
import { toDate } from "./timestamp";
import { RecipeIngredient } from "../models/Inventory";
import { Batch } from "../models/Batch";

/**
 * Returns ABV based on gravity samples or HydroPi/iSpindel readings.
 */
export const getBatchAbv = async (
  batchId: string,
  tenant: string,
  type: "gravity" | "hydropi" | "ispindel" = "gravity",
  hydroPiColor?: string,
  startDate?: Timestamp,
  endDate?: Timestamp
): Promise<{ abvNow: number | null; abvFinal: number | null }> => {
  try {
    if (type === "gravity") {
      const filters: Filter<GravitySample>[] = [
        { field: "batchId", operator: "==", value: batchId },
      ];

      const samples = await fetchItemsWithFilter<GravitySample>(
        prefixCollection("gravity", tenant),
        filters
      );

      if (!samples.length) return { abvNow: null, abvFinal: null };

      const latest = samples.sort(
        (a: GravitySample, b: GravitySample) =>
          toDate(b.date).getTime() - toDate(a.date).getTime()
      )[0];

      return {
        abvNow: parseFloat(calculateABV(latest)),
        abvFinal: parseFloat(calculateFinalABV(latest)),
      };
    }

    if (!hydroPiColor || !startDate) {
      console.warn("Missing HydroPi color or start date");
      return { abvNow: null, abvFinal: null };
    }

    const filters: Filter<any>[] = [
      { field: "color", operator: "==", value: hydroPiColor },
      {
        field: "timestamp",
        operator: ">=",
        value: Timestamp.fromMillis(startDate.seconds * 1000),
      },
      {
        field: "timestamp",
        operator: "<=",
        value: Timestamp.fromMillis(
          (endDate?.seconds ?? Timestamp.now().seconds) * 1000
        ),
      },
    ];

    const rawSamples =
      type === "hydropi"
        ? await fetchItemsWithFilter<HydroPiSample>(
            prefixCollection("hydroPi", tenant),
            filters
          )
        : await fetchItemsWithFilter<iSpindelSample>(
            prefixCollection("ispindel", tenant),
            filters
          );

          const samples: HydroPiSample[] =
          type === "ispindel"
            ? (rawSamples as iSpindelSample[]).map((s) => ({
                timestamp: s.timestamp,
                avg_gravity: s.gravity,
                avg_temp: s.temperature ?? 20,
                avg_temp_c: s.temperature ?? 20,
                temp_stddev: 0,
                gravity_stddev: 0,
                color: s.color ?? "unknown",
                uuid: s.ID?.toString() ?? "unknown", // 👈 convert to string
                id: "", // optional: provide real ID if available
              }))
            : (rawSamples as HydroPiSample[]);
        

    if (!samples.length) return { abvNow: null, abvFinal: null };

    const sorted = samples.sort(
      (a, b) => a.timestamp.seconds - b.timestamp.seconds
    );

    const og = sorted[0]?.avg_gravity ?? 1;
    const sg = sorted[sorted.length - 1]?.avg_gravity ?? 1;

    return {
      abvNow: parseFloat(calculateABVSimple(og, sg)),
      abvFinal: parseFloat(calculateABVSimple(og, 1.0)),
    };
  } catch (error) {
    console.error("Error calculating ABV for batch", batchId, error);
    return { abvNow: null, abvFinal: null };
  }
};

  export function getFinalAbvFromHydroPi(samples: HydroPiSample[]): number {
    if (!samples || samples.length < 2) return 0;
    const sorted = [...samples].sort(
      (a, b) => a.timestamp.seconds - b.timestamp.seconds
    );
    const og = sorted[0].avg_gravity;
    const fg = sorted[sorted.length - 1].avg_gravity;
    return calculateABVSimpleNumber(og, fg);
  }


export function getFinalAbvFromManualSamples(samples: GravitySample[]): number | undefined {
  if (!samples || samples.length < 2) return undefined;

  const sorted = [...samples].sort((a, b) => toDate(a.date).getTime() - toDate(b.date).getTime());

  const og = parseFloat(sorted[0]?.og);
  const fg = parseFloat(sorted[sorted.length - 1]?.fg);

  if (!og || !fg) return undefined;

  return calculateABVSimpleNumber(og, fg);
}

export function getAdjustedFinalAbvFromManualSamples(samples: GravitySample[], batch: Batch): number | undefined {
  if (!samples || samples.length < 2) return undefined;

  const sorted = [...samples].sort((a, b) => toDate(a.date).getTime() - toDate(b.date).getTime());

  const og = parseFloat(sorted[0]?.og);
  const fg = parseFloat(sorted[sorted.length - 1]?.fg);

  if (!og || !fg) return undefined;

  let totalSugarGrams = 0;

  if (batch.recipe?.ingredients?.length) {
    batch.recipe.ingredients.forEach((ingredient: RecipeIngredient) => {
      if (!ingredient.item?.sugarContentPer100g) return; // Only fruits

      const quantity = ingredient.quantity ?? 0;
      const unit = ingredient.item.unit?.toLowerCase() ?? "kg"; // Default to kg if missing
      const sugarContent = ingredient.item.sugarContentPer100g ?? 0; // g/100g

      let quantityKg = quantity;

      if (unit === "g" || unit === "gram" || unit === "grams") {
        quantityKg = quantity / 1000; // Convert g → kg
      } else if (unit === "mg") {
        quantityKg = quantity / 1_000_000; // Convert mg → kg
      }
      // else assume it's already kg

      const sugarGrams = quantityKg * 1000 * (sugarContent / 100) * 0.9; // 90% extraction

      totalSugarGrams += sugarGrams;
    });
  }

  const volumeLiters = batch.volume ?? batch.quantity ?? 1;
  const extraGP = totalSugarGrams / (volumeLiters * 10); // 10g sugar per L = +1 GP approx
  const extraGravity = extraGP / 1000; // GP to SG
  const adjustedOg = og + extraGravity;

  const abv = calculateABVSimpleNumber(adjustedOg, fg);

  return abv;
}

